import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService, DynamicAuditLogEnhanced, DynamicEntity } from './dynamic-api.service';

type SelectOption = { label: string; value: string | null };

@Component({
  standalone: true,
  selector: 'ain-dynamic-audit',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    SelectModule,
    FormsModule,
    JalaliDateInputComponent,
    AinDatePipe
  ],
  template: `
    <ain-page [title]="t('dynamic.audit.title')" [description]="t('dynamic.audit.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.audit.infoTitle'"
        [descriptionKey]="'dynamic.audit.infoDescription'"
        [bulletKeys]="[
          'dynamic.audit.infoBulletOperations',
          'dynamic.audit.infoBulletFilters',
          'dynamic.audit.infoBulletRetention'
        ]" />

      <div class="filters card">
        <label>
          <span>{{ t('dynamic.audit.filter.entity') }}</span>
          <p-select
            [options]="entityOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="entityFilter"
            name="entityFilter"
            [showClear]="true"
            [filter]="true"
            appendTo="body" />
        </label>
        <label>
          <span>{{ t('dynamic.audit.filter.operation') }}</span>
          <p-select
            [options]="operationOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="operationFilter"
            name="operationFilter"
            [showClear]="true"
            [filter]="true"
            appendTo="body" />
        </label>
        <label>
          <span>{{ t('dynamic.audit.filter.from') }}</span>
          <ain-date-input [(ngModel)]="fromDate" name="fromDate" />
        </label>
        <label>
          <span>{{ t('dynamic.audit.filter.to') }}</span>
          <ain-date-input [(ngModel)]="toDate" name="toDate" />
        </label>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!filteredLogs().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
      } @else {
        <p-table [value]="filteredLogs()" [paginator]="true" [rows]="15" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('dynamic.audit.columns.operation') }}</th>
              <th>{{ t('dynamic.audit.columns.entity') }}</th>
              <th>{{ t('dynamic.audit.columns.summary') }}</th>
              <th>{{ t('dynamic.audit.columns.actor') }}</th>
              <th>{{ t('dynamic.audit.columns.date') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ t(row.operationLabelKey) }}</td>
              <td>{{ row.entityDisplayName }}</td>
              <td>{{ row.operationSummary || '—' }}</td>
              <td>{{ row.actorUserId ? t('dynamic.audit.actorKnown') : t('dynamic.audit.actorSystem') }}</td>
              <td>{{ row.occurredAtUtc | ainDate }}</td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .filters {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
      margin-bottom: 16px;
    }

    .filters label {
      display: grid;
      gap: 6px;
      font-size: 0.875rem;
      color: #475569;
    }

    .card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fff;
    }

    @media (max-width: 960px) {
      .filters { grid-template-columns: 1fr; }
    }
  `]
})
export class DynamicAuditComponent implements OnInit {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly logs = signal<DynamicAuditLogEnhanced[]>([]);
  readonly entities = signal<DynamicEntity[]>([]);
  readonly loading = signal(true);
  readonly error = signal('');

  entityFilter: string | null = null;
  operationFilter: string | null = null;
  fromDate = this.daysAgoIso(30);
  toDate = this.todayIso();

  readonly entityOptions = computed<SelectOption[]>(() => [
    { label: this.t('common.all'), value: null },
    ...this.entities().map(entity => ({
      label: entity.displayName || entity.name,
      value: entity.id
    }))
  ]);

  readonly operationOptions = computed<SelectOption[]>(() => {
    const keys = [...new Set(this.logs().map(item => item.operationLabelKey))].sort();
    return [
      { label: this.t('common.all'), value: null },
      ...keys.map(key => ({ label: this.t(key), value: key }))
    ];
  });

  readonly filteredLogs = computed(() => {
    const fromMs = Date.parse(this.toUtcBoundary(this.fromDate, false));
    const toMs = Date.parse(this.toUtcBoundary(this.toDate, true));

    return this.logs().filter(row => {
      if (this.entityFilter && row.dynamicEntityId !== this.entityFilter) {
        return false;
      }
      if (this.operationFilter && row.operationLabelKey !== this.operationFilter) {
        return false;
      }
      const occurredMs = Date.parse(row.occurredAtUtc);
      return occurredMs >= fromMs && occurredMs <= toMs;
    });
  });

  ngOnInit(): void {
    this.api.listEntities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: items => this.entities.set(items),
      error: () => this.entities.set([])
    });
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.api.getAuditEnhanced().pipe(
      finalize(() => this.loading.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: items => {
        this.logs.set(items);
        this.error.set('');
      },
      error: () => {
        this.logs.set([]);
        this.error.set(this.t('common.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private daysAgoIso(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().slice(0, 10);
  }

  private todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private toUtcBoundary(dateValue: string, endOfDay: boolean): string {
    const suffix = endOfDay ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
    return `${dateValue}${suffix}`;
  }
}
